/**
 * GuidedOnboardingPage — 7-step guided wizard ported from pre-nuke legacy
 * (apps/dash-legacy/src/routes/_authed/onboarding.tsx).
 *
 * Legacy shape: zustand onboarding-state machine with steps
 *   tenant → tracking_domain → advertiser → offer → affiliate → test_tracking → finish
 * Here: plain React state machine (no zustand dep), same 7 steps, single POST
 * to `/api/v1/guided-onboarding` on finish. Design: light theme, Inter,
 * emerald→sky gradient H1, zinc-200 borders, 6-8px rounding.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useGuidedOnboardingCreate } from "./hooks";

type StepId =
	| "tenant"
	| "tracking_domain"
	| "advertiser"
	| "offer"
	| "affiliate"
	| "test_tracking"
	| "finish";

const STEPS: { id: StepId; label: string }[] = [
	{ id: "tenant", label: "Tenant" },
	{ id: "tracking_domain", label: "Tracking domain" },
	{ id: "advertiser", label: "First advertiser" },
	{ id: "offer", label: "First offer" },
	{ id: "affiliate", label: "First affiliate" },
	{ id: "test_tracking", label: "Test tracking" },
	{ id: "finish", label: "Finish" },
];

const CARD = "rounded-lg border border-zinc-200 bg-white p-6 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const LABEL = "text-sm font-medium text-zinc-700";
const BTN =
	"rounded-md border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

interface WizardState {
	tenant_name: string;
	tracking_domain: string;
	advertiser_name: string;
	offer_name: string;
	offer_payout: string;
	affiliate_name: string;
	affiliate_email: string;
	tracking_tested: boolean;
}

const INITIAL: WizardState = {
	tenant_name: "",
	tracking_domain: "",
	advertiser_name: "",
	offer_name: "",
	offer_payout: "",
	affiliate_name: "",
	affiliate_email: "",
	tracking_tested: false,
};

export function GuidedOnboardingPage() {
	const createMut = useGuidedOnboardingCreate();
	const [stepIdx, setStepIdx] = useState<number>(0);
	const [state, setState] = useState<WizardState>(INITIAL);
	const [done, setDone] = useState(false);

	const step = STEPS[stepIdx];
	const isLast = stepIdx === STEPS.length - 1;
	const isFirst = stepIdx === 0;

	const canProceed = useMemo(() => {
		switch (step.id) {
			case "tenant":
				return state.tenant_name.trim().length > 0;
			case "tracking_domain":
				return state.tracking_domain.trim().length > 0;
			case "advertiser":
				return state.advertiser_name.trim().length > 0;
			case "offer":
				return (
					state.offer_name.trim().length > 0 &&
					state.offer_payout.trim().length > 0
				);
			case "affiliate":
				return (
					state.affiliate_name.trim().length > 0 &&
					state.affiliate_email.trim().length > 0
				);
			case "test_tracking":
				return state.tracking_tested;
			case "finish":
				return true;
			default:
				return false;
		}
	}, [step.id, state]);

	const goNext = () =>
		setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
	const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

	const handleFinish = (e: FormEvent) => {
		e.preventDefault();
		createMut.mutate(
			{
				tenant_name: state.tenant_name.trim(),
				tracking_domain: state.tracking_domain.trim(),
				advertiser_name: state.advertiser_name.trim(),
				offer_name: state.offer_name.trim(),
				offer_payout: state.offer_payout.trim(),
				affiliate_name: state.affiliate_name.trim(),
				affiliate_email: state.affiliate_email.trim(),
				tracking_tested: state.tracking_tested,
			},
			{ onSuccess: () => setDone(true) },
		);
	};

	const update = <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
		setState((s) => ({ ...s, [key]: value }));

	return (
		<main
			data-testid="guided-onboarding-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10 font-[Inter,system-ui,sans-serif]"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Guided Onboarding
				</h1>
				<p
					className="text-xs font-medium uppercase tracking-wider text-zinc-500"
					data-testid="guided-onboarding-step-indicator"
				>
					Step {stepIdx + 1} of {STEPS.length} — {step.label}
				</p>
			</header>

			<ol
				className="flex flex-wrap items-center gap-2 text-xs"
				data-testid="guided-onboarding-stepper"
			>
				{STEPS.map((s, i) => {
					const active = i === stepIdx;
					const complete = i < stepIdx;
					return (
						<li
							key={s.id}
							className={
								"rounded-md border px-2.5 py-1 " +
								(active
									? "border-sky-500 bg-sky-50 text-sky-700"
									: complete
									? "border-emerald-500 bg-emerald-50 text-emerald-700"
									: "border-zinc-200 bg-white text-zinc-500")
							}
							data-testid={`guided-onboarding-step-${s.id}-marker`}
							aria-current={active ? "step" : undefined}
						>
							{i + 1}. {s.label}
						</li>
					);
				})}
			</ol>

			<form
				onSubmit={handleFinish}
				className={`${CARD} flex flex-col gap-5`}
				data-testid="guided-onboarding-wizard"
			>
				{step.id === "tenant" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-tenant"
					>
						<label className={LABEL} htmlFor="go-tenant-name">
							Workspace / tenant name
						</label>
						<input
							id="go-tenant-name"
							type="text"
							className={INPUT}
							value={state.tenant_name}
							onChange={(e) => update("tenant_name", e.currentTarget.value)}
							data-testid="guided-onboarding-input-tenant-name"
						/>
						<p className="text-xs text-zinc-500">
							We use this to label your data in reports.
						</p>
					</div>
				) : step.id === "tracking_domain" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-tracking-domain"
					>
						<label className={LABEL} htmlFor="go-tracking-domain">
							Tracking domain
						</label>
						<input
							id="go-tracking-domain"
							type="text"
							placeholder="track.example.com"
							className={INPUT}
							value={state.tracking_domain}
							onChange={(e) =>
								update("tracking_domain", e.currentTarget.value)
							}
							data-testid="guided-onboarding-input-tracking-domain"
						/>
						<p className="text-xs text-zinc-500">
							The subdomain that will receive click and conversion pixels.
						</p>
					</div>
				) : step.id === "advertiser" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-advertiser"
					>
						<label className={LABEL} htmlFor="go-advertiser-name">
							First advertiser
						</label>
						<input
							id="go-advertiser-name"
							type="text"
							className={INPUT}
							value={state.advertiser_name}
							onChange={(e) =>
								update("advertiser_name", e.currentTarget.value)
							}
							data-testid="guided-onboarding-input-advertiser-name"
						/>
					</div>
				) : step.id === "offer" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-offer"
					>
						<label className={LABEL} htmlFor="go-offer-name">
							First offer
						</label>
						<input
							id="go-offer-name"
							type="text"
							className={INPUT}
							value={state.offer_name}
							onChange={(e) => update("offer_name", e.currentTarget.value)}
							data-testid="guided-onboarding-input-offer-name"
						/>
						<label className={LABEL} htmlFor="go-offer-payout">
							Payout (USD)
						</label>
						<input
							id="go-offer-payout"
							type="number"
							min="0"
							step="0.01"
							className={INPUT}
							value={state.offer_payout}
							onChange={(e) => update("offer_payout", e.currentTarget.value)}
							data-testid="guided-onboarding-input-offer-payout"
						/>
					</div>
				) : step.id === "affiliate" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-affiliate"
					>
						<label className={LABEL} htmlFor="go-affiliate-name">
							First affiliate
						</label>
						<input
							id="go-affiliate-name"
							type="text"
							className={INPUT}
							value={state.affiliate_name}
							onChange={(e) =>
								update("affiliate_name", e.currentTarget.value)
							}
							data-testid="guided-onboarding-input-affiliate-name"
						/>
						<label className={LABEL} htmlFor="go-affiliate-email">
							Contact email
						</label>
						<input
							id="go-affiliate-email"
							type="email"
							className={INPUT}
							value={state.affiliate_email}
							onChange={(e) =>
								update("affiliate_email", e.currentTarget.value)
							}
							data-testid="guided-onboarding-input-affiliate-email"
						/>
					</div>
				) : step.id === "test_tracking" ? (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-test-tracking"
					>
						<p className="text-sm text-zinc-700">
							Send a test click to {state.tracking_domain || "your tracking domain"} and confirm
							you see it in the live feed.
						</p>
						<label className="flex items-center gap-2 text-sm text-zinc-700">
							<input
								type="checkbox"
								checked={state.tracking_tested}
								onChange={(e) =>
									update("tracking_tested", e.currentTarget.checked)
								}
								data-testid="guided-onboarding-input-tracking-tested"
							/>
							Tracking test completed.
						</label>
					</div>
				) : (
					<div
						className="flex flex-col gap-3"
						data-testid="guided-onboarding-step-finish"
					>
						<p className="text-sm text-zinc-700">Review and submit.</p>
						<dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
							<dt className="text-zinc-500">Tenant</dt>
							<dd className="text-zinc-900">{state.tenant_name || "-"}</dd>
							<dt className="text-zinc-500">Tracking domain</dt>
							<dd className="text-zinc-900">
								{state.tracking_domain || "-"}
							</dd>
							<dt className="text-zinc-500">Advertiser</dt>
							<dd className="text-zinc-900">
								{state.advertiser_name || "-"}
							</dd>
							<dt className="text-zinc-500">Offer</dt>
							<dd className="text-zinc-900">
								{state.offer_name
									? `${state.offer_name} ($${state.offer_payout || "0"})`
									: "-"}
							</dd>
							<dt className="text-zinc-500">Affiliate</dt>
							<dd className="text-zinc-900">
								{state.affiliate_name
									? `${state.affiliate_name} <${state.affiliate_email}>`
									: "-"}
							</dd>
							<dt className="text-zinc-500">Tracking tested</dt>
							<dd className="text-zinc-900">
								{state.tracking_tested ? "yes" : "no"}
							</dd>
						</dl>
						{done ? (
							<p
								className="text-sm text-emerald-700"
								role="status"
								data-testid="guided-onboarding-done"
							>
								Onboarding submitted.
							</p>
						) : null}
						{createMut.error ? (
							<p
								className="text-sm text-red-600"
								role="alert"
								data-testid="guided-onboarding-error"
							>
								{createMut.error.message}
							</p>
						) : null}
					</div>
				)}

				<div className="mt-2 flex items-center justify-between">
					<button
						type="button"
						onClick={goBack}
						disabled={isFirst}
						className={BTN_GHOST}
						data-testid="guided-onboarding-back"
					>
						Back
					</button>
					{!isLast ? (
						<button
							type="button"
							onClick={goNext}
							disabled={!canProceed}
							className={BTN}
							data-testid="guided-onboarding-next"
						>
							Next
						</button>
					) : (
						<button
							type="submit"
							className={BTN}
							disabled={createMut.isPending || done}
							data-testid="guided-onboarding-submit"
						>
							{createMut.isPending
								? "Submitting."
								: done
								? "Submitted"
								: "Submit"}
						</button>
					)}
				</div>
			</form>
		</main>
	);
}

export default GuidedOnboardingPage;
